/*
 * Early interaction binding.
 *
 * The reported bug was that Size Guide does nothing until the page has
 * finished loading completely. That is not a race or a flaky selector - it is
 * where the theme chooses to bind:
 *
 *     theme.js:24    window.onload -> halo.initProductPage()
 *                                  -> halo.initProductView()
 *                                  -> halo.productSizeChart($scope)
 *                                  -> $btn.on('click', ...)
 *
 * window.onload does not fire until every image, iframe, font and third-party
 * script on the page has finished. On a product page here that means Air
 * Reviews, Instafeed, MailerLite, the Echo chat widget, three gtag containers
 * and the merchant badge iframe all have to settle first. Until they do, the
 * Size Guide link is markup with nothing listening to it, so a tap does
 * nothing at all and the shopper concludes the site is broken.
 *
 * The same applies to the cart quantity steppers. global.js:720 registers
 * <cart-update-quantity> and <quickshop-update-quantity> inside a
 * window.onload handler, so those elements stay un-upgraded - and therefore
 * inert - for exactly as long.
 *
 * This file binds those three controls at deferred-script time instead, which
 * is immediately after the HTML is parsed and long before onload. It is loaded
 * first in global-script-2.liquid so it runs ahead of theme.js rather than
 * behind 352 KB of it.
 *
 * WHAT THIS DOES NOT DO
 *
 * It does not replace anything. theme.js and global.js still run exactly as
 * they did, at exactly the same moment. This only makes the controls work
 * during the window before that happens, and hands back cleanly once it does -
 * see the two notes below on why there is no double-firing.
 *
 * Deliberately left alone: <product-scroller> and <fade-in-component>, the
 * other two elements registered at onload in global.js. ProductScroller's
 * constructor measures clientWidth and compares it against scrollWidth to
 * decide which element to attach drag-scrolling to. Those measurements are
 * only meaningful once images have dimensions, which is what onload actually
 * guarantees, so moving it earlier would make it read the wrong widths. It has
 * no dead-button symptom either way. FadeInComponent is a scroll animation,
 * not a control.
 */

(function () {
  'use strict';

  var CE = window.customElements;

  /* ------------------------------------------------------------------ 1 --
   * Make customElements.define idempotent.
   *
   * This is required, not defensive. Registering an element here does not
   * stop global.js from trying to register it again at onload, and the second
   * call throws NotSupportedError. That matters because the three defines sit
   * in one arrow function:
   *
   *     customElements.define('cart-update-quantity', UpdateQuantity);
   *     customElements.define('quickshop-update-quantity', UpdateQuantityQuickShop);
   *     customElements.define('product-scroller', ProductScroller);
   *
   * so an uncaught throw on the first line would take <product-scroller> down
   * with it - a working feature broken by a fix to a different one. Swallowing
   * the duplicate turns those first two lines into no-ops and lets the third
   * register normally.
   */
  if (CE && typeof CE.define === 'function' && !CE.civiaIdempotentDefine) {
    var nativeDefine = CE.define.bind(CE);

    CE.define = function (name, constructor, options) {
      if (CE.get(name)) return;
      return nativeDefine(name, constructor, options);
    };

    CE.civiaIdempotentDefine = true;
  }

  /* ------------------------------------------------------------------ 2 --
   * Upgrade the quantity steppers now rather than at onload.
   *
   * UpdateQuantity and UpdateQuantityQuickShop are top-level class
   * declarations in global.js, which is a classic script loaded synchronously
   * in <head>. That puts them in the global lexical environment - reachable by
   * bare name from here, but not as properties of window, so they have to be
   * named directly rather than looked up as window['UpdateQuantity'].
   *
   * Both constructors only ever call querySelector and addEventListener on
   * their own subtree. Neither measures layout or touches an image, so there
   * is nothing in them that needs onload to have happened. Checked both before
   * moving them; that is the whole reason product-scroller stayed behind.
   *
   * Each is wrapped separately so that a failure in one cannot stop the other,
   * and neither can stop the size chart below.
   */
  function defineEarly(tagName, constructor) {
    if (!CE) return;

    try {
      if (typeof constructor === 'function') CE.define(tagName, constructor);
    } catch (error) {
      /* An un-upgraded element is the status quo, not a regression: global.js
       * still gets its turn at onload. Never let this throw past here. */
    }
  }

  try {
    defineEarly('cart-update-quantity', typeof UpdateQuantity === 'function' ? UpdateQuantity : null);
  } catch (error) {}

  try {
    defineEarly('quickshop-update-quantity', typeof UpdateQuantityQuickShop === 'function' ? UpdateQuantityQuickShop : null);
  } catch (error) {}

  /* ------------------------------------------------------------------ 3 --
   * Size chart popup.
   *
   * Both classes are load-bearing: component-size-chart.css shows the popup on
   *
   *     body.size-chart-show .halo-size-chart-popup.is-show
   *
   * so setting one without the other leaves it invisible. The quick-view
   * branch mirrors what theme.js does - in quick view the popup has been moved
   * inside .halo-popup-content and given a different id, so it has to be found
   * by a different selector.
   */
  function sizeChartPopup() {
    var selector = document.body.classList.contains('quick-view-show')
      ? '.halo-popup-content .halo-size-chart-popup'
      : '#MainContent .halo-size-chart-popup';

    return document.querySelector(selector);
  }

  function openSizeChart() {
    var popup = sizeChartPopup();

    if (!popup) return;

    document.body.classList.add('size-chart-show');
    popup.classList.add('is-show');
  }

  function closeSizeChart() {
    var popups = document.querySelectorAll('.halo-size-chart-popup');

    document.body.classList.remove('size-chart-show');

    for (var i = 0; i < popups.length; i++) {
      popups[i].classList.remove('is-show');
    }
  }

  /* Bubble phase on document, deliberately.
   *
   * Once theme.js has run it binds its own handler directly to the trigger and
   * calls event.stopPropagation() inside it. A bubble-phase listener up here
   * is therefore never reached, so from onload onwards theme.js is the only
   * thing that opens the chart and this listener silently stands down. That is
   * the whole reason it is not on capture: capture would fire first, run
   * alongside theme.js forever, and open the popup twice.
   *
   * The same trigger already has a capture-phase listener in
   * halo-size-chart-popup.liquid, which highlights the shopper's selected row.
   * It schedules its work with setTimeout(0), so it still lands after this
   * handler has opened the popup and finds the table on screen as it expects.
   *
   * Close is not affected either way - theme.js binds close on document too,
   * with no stopPropagation, so after onload both run and both do the same
   * idempotent class removal.
   */
  document.addEventListener('click', function (event) {
    var target = event.target;

    if (!target || !target.closest) return;

    if (target.closest('[data-open-size-chart-popup]')) {
      event.preventDefault();
      openSizeChart();
      return;
    }

    if (!document.body.classList.contains('size-chart-show')) return;

    if (target.closest('[data-close-size-chart-popup]')) {
      event.preventDefault();
      closeSizeChart();
      return;
    }

    /* Click outside the popup closes it, matching theme.js. */
    if (!target.closest('[data-size-chart-popup]')) closeSizeChart();
  }, false);
})();
